/**
 * @fileoverview localStorage persistence for Custom Mosaics colorways.
 *
 * One entry per pattern, shaped like the brief's state object:
 * `{ patternId, grout: { id, hex }, colors: { [tileId]: { name, hex } } }`.
 * Tile ids are stable, so saved colorways survive data refreshes; colors for
 * ids that no longer exist are dropped on load.
 *
 * @module persistence
 */

const PREFIX = 'fct.customMosaics.v1';
const LAST_PATTERN_KEY = `${PREFIX}.lastPattern`;

function storage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

/**
 * @param {string} patternId
 * @returns {string}
 */
export function designKey(patternId) {
    return `${PREFIX}.design.${patternId}`;
}

/**
 * Saves a design. A design with no painted tiles removes the saved entry so a
 * reset sticks. Silently ignores quota/privacy-mode failures.
 * @param {{patternId: string, grout: Object, colors: Object}} design
 */
export function saveDesign(design) {
    const s = storage();
    if (!s || !design?.patternId) return;
    try {
        if (Object.keys(design.colors || {}).length === 0) {
            s.removeItem(designKey(design.patternId));
        } else {
            s.setItem(designKey(design.patternId), JSON.stringify(design));
        }
        s.setItem(LAST_PATTERN_KEY, design.patternId);
    } catch {
        // ignore
    }
}

/**
 * Loads a saved design for a pattern.
 * @param {Object} pattern - Pattern definition (used to drop stale tile ids)
 * @returns {{grout: Object|null, colors: Object}|null}
 */
export function loadDesign(pattern) {
    const s = storage();
    if (!s || !pattern) return null;
    try {
        const raw = s.getItem(designKey(pattern.id));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const validIds = new Set(pattern.tiles.map((t) => t.id));
        const colors = {};
        for (const [id, c] of Object.entries(parsed?.colors || {})) {
            if (validIds.has(id) && c?.name && /^#[0-9a-fA-F]{6}$/.test(c.hex)) {
                colors[id] = { name: c.name, hex: c.hex };
            }
        }
        return { grout: parsed?.grout || null, colors };
    } catch {
        return null;
    }
}

/**
 * @returns {string|null} The most recently edited pattern id
 */
export function loadLastPatternId() {
    try {
        return storage()?.getItem(LAST_PATTERN_KEY) || null;
    } catch {
        return null;
    }
}
